import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import useStyles from "./styles";
import api from "../../../services/api";
import corNeutraChip from "../corNeutraChip";

// Classifica cada cliente do resultado da rotina em massa - nunca decidir "deu certo" olhando so
// coletaOk: um cliente pode ter coletaOk:true e ainda assim nao ter sido processado, por causa de
// erroInterno na leitura pos-coleta (nesse caso atualizados/pulados/falhados sempre vem vazios)
function statusCliente(cliente) {
	if (!cliente?.coletaOk) return "falha_coleta";
	if (cliente?.erroInterno) return "erro_interno";
	return "processado";
}

export default function ModalAtualizarTodos({ open, onClose, templates, onConcluido }) {
	const [confirmado, setConfirmado] = useState(false);
	const [enviando, setEnviando] = useState(false);
	const [resultado, setResultado] = useState(null);
	const [erroEnvio, setErroEnvio] = useState(null);
	const theme = useTheme();
	const styles = useStyles(theme);

	// Reseta o estado toda vez que o modal reabre - o resultado de uma rodada anterior nao pode
	// vazar pra proxima abertura
	useEffect(() => {
		if (!open) return;
		setConfirmado(false);
		setEnviando(false);
		setResultado(null);
		setErroEnvio(null);
	}, [open]);

	// POST /versionamento/templates/rotina sem clienteId - atualiza todos os clientes de uma vez,
	// a escrita mais ampla desta feature. Erro de rede/500 aqui e generico, nao se confunde com
	// falha individual de cliente (que ja vem dentro de resumoGeral/clientes)
	async function handleConfirmarAtualizacao() {
		setEnviando(true);
		setErroEnvio(null);
		try {
			const response = await api.post("/versionamento/templates/rotina", {});
			setResultado(response?.data || null);
		} catch (error) {
			setErroEnvio(
				error?.response?.data?.error ||
					"Não foi possível enviar a atualização. Tente novamente.",
			);
		} finally {
			setEnviando(false);
		}
	}

	// Fecha o modal - se ja havia um resultado (rotina enviada), dispara o refresh do grid do pai antes
	function handleFechar() {
		if (enviando) return;
		if (resultado) {
			onConcluido?.();
		}
		onClose?.();
	}

	// Par fundo/texto para as duas situacoes de cliente que nunca chegaram a ser processadas
	// (falha_coleta e erro_interno) e para o caso neutro "nada a atualizar" - corNeutraChip (nao o
	// chip.background/colorText.default cru, que e quase transparente e ilegivel nos dois modos)
	function corSituacaoCliente(situacao) {
		if (situacao === "falha") {
			return {
				background: theme.palette.chip.background.critical,
				color: theme.palette.chip.colorText.critical,
			};
		}
		return corNeutraChip(theme);
	}

	const listaTemplates = templates || [];
	// Todos os templates com pelo menos 1 cliente desatualizado, independente de bloqueio por
	// divergencia - o bloqueio so e decidido depois, na chamada de verdade
	const templatesAfetados = listaTemplates.filter((template) => template?.desatualizado > 0);
	const clientesAfetados = new Set();
	templatesAfetados.forEach((template) => {
		template?.clientes?.forEach((cliente) => {
			if (cliente?.status === "Desatualizado") {
				clientesAfetados.add(cliente?.clienteId);
			}
		});
	});

	const resumoGeral = resultado?.resumoGeral;
	const clientesResultado = resultado?.clientes || [];
	const corFalha = corSituacaoCliente("falha");
	const corNeutra = corSituacaoCliente("neutro");

	return (
		<Dialog open={open} onClose={handleFechar} fullWidth maxWidth="sm">
			<DialogTitle>Atualizar todos os templates desatualizados</DialogTitle>
			<DialogContent>
				{!resultado && (
					<Box>
						<Typography variant="body2" sx={styles.resumo}>
							<strong>{templatesAfetados.length} templates desatualizados</strong> afetando{" "}
							<strong>{clientesAfetados.size} clientes</strong>
						</Typography>
						<Box sx={styles.listaTemplates}>
							{templatesAfetados.map((template) => (
								<Box key={template?.chave} sx={styles.linhaTemplate}>
									<Typography variant="body2" sx={styles.nomeTemplate}>
										{template?.nome || template?.chave}
									</Typography>
									<Typography variant="caption" sx={styles.contagemTemplate}>
										{template?.desatualizado} cliente(s) desatualizado(s)
									</Typography>
								</Box>
							))}
						</Box>
						<Typography variant="body2" sx={styles.aviso}>
							Itens com divergência serão pulados automaticamente e registrados no
							histórico — nenhuma customização local será sobrescrita.
						</Typography>
						<Box sx={styles.confirmacaoContainer}>
							<FormControlLabel
								control={
									<Checkbox
										checked={confirmado}
										disabled={enviando}
										onChange={(event) => setConfirmado(event.target.checked)}
									/>
								}
								label="Confirmo que quero atualizar os templates desatualizados de todos os clientes listados"
							/>
						</Box>
						{erroEnvio && (
							<Typography variant="body2" sx={styles.erro}>
								{erroEnvio}
							</Typography>
						)}
					</Box>
				)}
				{resultado && (
					<Box sx={styles.resultadoContainer}>
						<Box sx={styles.resumoGeralContainer}>
							<Chip
								size="small"
								label={`${resumoGeral?.templatesAtualizados || 0} atualizados`}
								sx={{
									...styles.chip,
									backgroundColor: theme.palette.chip.background.success,
									color: theme.palette.chip.colorText.success,
								}}
							/>
							<Chip
								size="small"
								label={`${resumoGeral?.templatesPulados || 0} pulados por divergência`}
								sx={{
									...styles.chip,
									backgroundColor: theme.palette.chip.background.high,
									color: theme.palette.chip.colorText.high,
								}}
							/>
							<Chip
								size="small"
								label={`${resumoGeral?.templatesFalhados || 0} falhados`}
								sx={{
									...styles.chip,
									backgroundColor: theme.palette.chip.background.critical,
									color: theme.palette.chip.colorText.critical,
								}}
							/>
						</Box>
						{clientesResultado.length === 0 && (
							<Typography variant="body2" sx={styles.semItens}>
								Nenhum cliente foi processado.
							</Typography>
						)}
						{clientesResultado.map((cliente) => {
							const situacao = statusCliente(cliente);
							const nadaAAtualizar =
								situacao === "processado" &&
								!cliente?.atualizados?.length &&
								!cliente?.pulados?.length &&
								!cliente?.falhados?.length;
							return (
								<Box key={cliente?.clienteId} sx={styles.linhaResultado}>
									<Typography variant="body2" sx={styles.nomeResultado}>
										{cliente?.nome || cliente?.clienteId}
									</Typography>
									{situacao === "falha_coleta" && (
										<Tooltip title={cliente?.motivoFalhaColeta || ""}>
											<Chip
												size="small"
												label="Falha na coleta"
												sx={{
													...styles.chip,
													backgroundColor: corFalha.background,
													color: corFalha.color,
												}}
											/>
										</Tooltip>
									)}
									{situacao === "erro_interno" && (
										<Tooltip title={cliente?.erroInterno || ""}>
											<Chip
												size="small"
												label="Erro interno"
												sx={{
													...styles.chip,
													backgroundColor: corFalha.background,
													color: corFalha.color,
												}}
											/>
										</Tooltip>
									)}
									{situacao === "processado" && nadaAAtualizar && (
										<Chip
											size="small"
											label="Nada a atualizar"
											sx={{
												...styles.chip,
												backgroundColor: corNeutra.background,
												color: corNeutra.color,
											}}
										/>
									)}
									{situacao === "processado" && !nadaAAtualizar && (
										<Box sx={styles.contagensProcessado}>
											<Chip
												size="small"
												label={`${cliente?.atualizados?.length || 0} atualizado(s)`}
												sx={{
													...styles.chip,
													backgroundColor: theme.palette.chip.background.success,
													color: theme.palette.chip.colorText.success,
												}}
											/>
											{cliente?.pulados?.length > 0 && (
												<Tooltip
													title={cliente.pulados
														.map((pulado) => `${pulado?.chave}: ${pulado?.motivo}`)
														.join(" | ")}
												>
													<Chip
														size="small"
														label={`${cliente.pulados.length} pulado(s)`}
														sx={{
															...styles.chip,
															backgroundColor: theme.palette.chip.background.high,
															color: theme.palette.chip.colorText.high,
														}}
													/>
												</Tooltip>
											)}
											{cliente?.falhados?.length > 0 && (
												<Tooltip
													title={cliente.falhados
														.map((falhado) => `${falhado?.chave}: ${falhado?.motivo}`)
														.join(" | ")}
												>
													<Chip
														size="small"
														label={`${cliente.falhados.length} falhado(s)`}
														sx={{
															...styles.chip,
															backgroundColor: theme.palette.chip.background.critical,
															color: theme.palette.chip.colorText.critical,
														}}
													/>
												</Tooltip>
											)}
										</Box>
									)}
								</Box>
							);
						})}
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				{!resultado ? (
					<>
						<Button onClick={handleFechar} disabled={enviando}>
							Cancelar
						</Button>
						<Button
							variant="contained"
							disabled={enviando || !confirmado}
							onClick={handleConfirmarAtualizacao}
						>
							{enviando ? <CircularProgress size={20} /> : "Confirmar atualização"}
						</Button>
					</>
				) : (
					<Button variant="contained" onClick={handleFechar}>
						Fechar
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}
